import { useMutation } from '@apollo/client';
import { createError } from '@common/neo.tax';
import {
  Button,
  ModalHeader,
  ModalActions,
  colors,
  Select,
  InputFormContainer,
  Spinner,
  Typography,
  Stack,
} from '@ui-components/neo.tax';
import { ReactComponent as Calendar } from '@ui-components/neo.tax/dist/icons/calendar.svg';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CREATE_US_RD_FISCAL_YEAR,
  GetCompanyQueryVariables,
  GET_COMPANY,
  CreateUsRdFiscalYearMutationVariables,
  GET_ALL_FISCAL_YEARS,
  GetAllFiscalYearsQueryVariables,
  CreateUsRdFiscalYearMutation,
  PeriodName,
  ProductType,
} from '@neo.tax/shared/graphql';
import {
  useAllFiscalYears,
  useSelectedPeriodNameOptional,
  useSelectedFiscalYearIdOptional,
} from '@neo.tax/shared/hooks';
import {
  getDefaultAscPath,
  getDefaultRdPath,
  getDefaultSredPath,
  productDisplayName,
  ProductPeriodSelection,
} from '@neo.tax/shared/utils';

import { useNavigateTo } from '../navigate';
import { StyledTypography, StyledTypographyBlock } from './fiscal-period-modal.styled';
import { makeYearLabel } from './fiscal-period-modal.utils';
import {
  useFiscalPeriodModalAnalyticIds,
  useAscPeriodOptionsInSelectedTaxYear,
  AvailableFiscalYearOption,
  AvailableProducts,
} from './hooks';

interface FiscalPeriodSelectionProps {
  setIsOpen: (isOpen: boolean) => void;
  companyId: string;
  selectedProduct: ProductType;
  clearSelectedProduct: () => void;
  availableProducts: AvailableProducts;
  defaultFiscalYearId: string | null;
  onProductPeriodChange: (value: ProductPeriodSelection) => void;
}

export const FiscalPeriodSelection = ({
  availableProducts,
  clearSelectedProduct,
  companyId,
  defaultFiscalYearId,
  onProductPeriodChange,
  selectedProduct,
  setIsOpen,
}: FiscalPeriodSelectionProps): React.ReactElement | null => {
  const navigate = useNavigateTo();
  const {
    fiscalPeriodModalBackToProductSelection,
    fiscalPeriodModalCancelBtnId,
    fiscalPeriodModalPeriodSelect,
    fiscalPeriodModalSelectBtnId,
    fiscalPeriodModalYearSelect,
  } = useFiscalPeriodModalAnalyticIds();
  const [createUsRdFiscalYear, { loading: creatingFiscalYear }] = useMutation<
    CreateUsRdFiscalYearMutation,
    CreateUsRdFiscalYearMutationVariables
  >(CREATE_US_RD_FISCAL_YEAR, {
    awaitRefetchQueries: true,
    refetchQueries: companyId
      ? [
          {
            query: GET_ALL_FISCAL_YEARS,
            variables: {
              companyId,
            } satisfies GetAllFiscalYearsQueryVariables,
          },
          {
            query: GET_COMPANY,
            variables: {
              companyId,
            } satisfies GetCompanyQueryVariables,
          },
        ]
      : [],
  });

  const { fiscalYears } = useAllFiscalYears();
  const periodNameFromUrl = useSelectedPeriodNameOptional();
  const fiscalYearIdFromUrl = useSelectedFiscalYearIdOptional();
  const availableFiscalYears = availableProducts[selectedProduct];

  const isClosable = !!periodNameFromUrl;
  const [selectedFiscalYearOption, setSelectedFiscalYearOption] = useState<AvailableFiscalYearOption | null>(null);

  const selectedFiscalYear = useMemo(() => {
    if (!fiscalYears || !selectedFiscalYearOption) {
      return null;
    }
    const currentFiscalYear = fiscalYears.find((fiscalYear) => fiscalYear.id === selectedFiscalYearOption.id);
    return currentFiscalYear ?? fiscalYears.find((fiscalYear) => fiscalYear.id === defaultFiscalYearId) ?? null;
  }, [defaultFiscalYearId, fiscalYears, selectedFiscalYearOption]);

  const { defaultPeriodOption, periodOptions } = useAscPeriodOptionsInSelectedTaxYear({
    selectedFiscalYear,
    selectedProduct,
  });
  const [selectedPeriodNameOption, setSelectedPeriodNameOption] = useState<PeriodName | null>(null);

  const handleSelectedPeriodChange = ({ value }: { value: string }): void => {
    setSelectedPeriodNameOption(
      periodOptions?.flatMap((option) => option.options).find((o) => o.value === value)?.periodName ?? null,
    );
  };

  const handleSelectTaxYear = useCallback(
    (taxYear: number): void => {
      const selectedFiscalYearOption = availableFiscalYears.find((option) => option.year === taxYear) ?? null;
      if (!selectedFiscalYearOption) {
        return;
      }
      setSelectedFiscalYearOption(selectedFiscalYearOption);
      setSelectedPeriodNameOption(null);
    },
    [availableFiscalYears],
  );

  const handleSubmit = async (): Promise<void> => {
    if (!selectedFiscalYearOption) {
      return;
    }

    let fiscalYearId: string | null = selectedFiscalYearOption.id ?? null;

    /**
     * Product configuration: defines navigation and fiscal year behavior per product.
     *
     * ASC operates on periods within the fiscal year (quarters, half-years, or months), so the user must select a specific period.
     * R&D and SR&ED are annual claims on the full fiscal year.
     * The UI will require the user to select a period for ASC, but not for R&D or SR&ED.
     * - requiresPeriodName:
     *   - true if the product operates on periods within the fiscal year (quarters, half-years, or months),
     *   - false if it operates on annual periods (e.g. fiscal year).
     *
     * R&D is the entry-point product, so it can create a fiscal year on-the-fly if one doesn't exist yet.
     * ASC and SR&ED require an existing fiscal year (typically created via web-tools first).
     * - shouldCreateFiscalYearIfMissing:
     *   - true if the product can create a fiscal year on-the-fly if one doesn't exist yet,
     *   - false if the product requires an existing fiscal year (typically created via web-tools first).
     */
    const productConfig = {
      [ProductType.UsRd]: {
        requiresPeriodName: false,
        shouldCreateFiscalYearIfMissing: true,
        getPath: (companyId: string, fiscalYearId: string, _periodName: PeriodName) =>
          getDefaultRdPath({ companyId, fiscalYearId }),
      },
      [ProductType.UsAsc]: {
        requiresPeriodName: true,
        shouldCreateFiscalYearIfMissing: false,
        getPath: (companyId: string, fiscalYearId: string, periodName: PeriodName) =>
          getDefaultAscPath({ companyId, fiscalYearId, periodName }),
      },
      [ProductType.CaSred]: {
        requiresPeriodName: false,
        shouldCreateFiscalYearIfMissing: false,
        getPath: (companyId: string, fiscalYearId: string, _periodName: PeriodName) =>
          getDefaultSredPath({ companyId, fiscalYearId }),
      },
    } as const;

    const config = productConfig[selectedProduct];
    if (!config) {
      console.error(`Unsupported product type: ${selectedProduct}`);
      setIsOpen(false);
      return;
    }

    // Create fiscal year if needed and missing
    if (config.shouldCreateFiscalYearIfMissing && !fiscalYearId) {
      const data = await createUsRdFiscalYear({
        variables: {
          companyId,
          fiscalYearPeriod: selectedFiscalYearOption.fiscalYearPeriod,
        },
      });
      fiscalYearId = data.data?.createUsRdFiscalYear?.id ?? null;
      if (!fiscalYearId) {
        throw createError('FiscalYearIdNotFoundError', 'Fiscal year id not found after creating');
      }
    }

    // Validate required fields
    if (!fiscalYearId) {
      throw createError('FiscalYearIdNotFoundError', 'Fiscal year id not found');
    }

    if (config.requiresPeriodName && !selectedPeriodNameOption) {
      setIsOpen(false);
      return;
    }

    // Build navigation path based on product requirements
    const path = config.getPath(
      companyId,
      fiscalYearId,
      config.requiresPeriodName && selectedPeriodNameOption ? selectedPeriodNameOption : PeriodName.A,
    );

    // update state and navigate
    onProductPeriodChange({
      fiscalYearId,
      periodName: selectedPeriodNameOption ?? PeriodName.A,
      product: selectedProduct,
    });
    navigate(path);
    setIsOpen(false);
  };

  useEffect(() => {
    if (!periodOptions && selectedPeriodNameOption) {
      setSelectedPeriodNameOption(null);
      return;
    }
    if (!periodOptions || selectedPeriodNameOption) {
      return;
    }
    if (!periodNameFromUrl) {
      setSelectedPeriodNameOption(defaultPeriodOption?.periodName ?? null);
      return;
    }
    setSelectedPeriodNameOption(periodNameFromUrl);
  }, [defaultPeriodOption, periodNameFromUrl, periodOptions, selectedPeriodNameOption]);

  useEffect(() => {
    if (availableFiscalYears.length > 0) {
      const taxYearOption = fiscalYearIdFromUrl
        ? availableFiscalYears.find((option) => option.id === fiscalYearIdFromUrl)
        : availableFiscalYears[0];

      if (!taxYearOption) {
        return;
      }
      if (!selectedFiscalYearOption) {
        setSelectedFiscalYearOption(taxYearOption);
      }
    } else {
      if (selectedFiscalYearOption) {
        // wait for the modal's close animation to complete
        setTimeout(() => {
          setSelectedFiscalYearOption(null);
        }, 300);
      }
    }
  }, [fiscalYearIdFromUrl, selectedFiscalYearOption, availableFiscalYears]);

  const disabled = !selectedFiscalYearOption || creatingFiscalYear;

  return (
    <Stack direction="column">
      <ModalHeader
        icon={<Calendar width={24} color={colors.blue[600]} />}
        iconBgColor={colors.blue[50]}
        title={
          <Stack direction="column" alignItems="center">
            <Typography as="span" fontSize="inherit" fontWeight="inherit" color="inherit">
              Select Time Period
            </Typography>
            <Typography as="span" fontSize="inherit" fontWeight="inherit" color="inherit">
              ({selectedProduct ? productDisplayName(selectedProduct) : '----'})
            </Typography>
          </Stack>
        }
        copy={
          <Typography as="span" fontSize="sm" color="inherit">
            Please select the time period to examine.
            <StyledTypographyBlock fontSize="inherit" color="inherit">
              Or{' '}
              <StyledTypography
                id={fiscalPeriodModalBackToProductSelection}
                as="button"
                onClick={clearSelectedProduct}
                fontSize="sm"
                color={colors.primary[600]}
                textDecoration="underline"
              >
                select a different product
              </StyledTypography>
            </StyledTypographyBlock>
          </Typography>
        }
      />
      <InputFormContainer inputId={fiscalPeriodModalYearSelect} label="Tax Year" horizontal>
        <Select
          id={fiscalPeriodModalYearSelect}
          autoFocus
          placeholder="Select Tax Year"
          required
          value={selectedFiscalYearOption?.year.toString()}
          onChange={(value): void => {
            handleSelectTaxYear(Number(value));
          }}
          options={availableFiscalYears.map(({ fiscalYearPeriod, year }) => ({
            value: year.toString(),
            label: makeYearLabel(fiscalYearPeriod.endMonthYear),
          }))}
          usePortal
        />
      </InputFormContainer>
      {selectedProduct === ProductType.UsAsc && (
        <InputFormContainer inputId={fiscalPeriodModalPeriodSelect} label="Period" horizontal>
          <Select
            id={fiscalPeriodModalPeriodSelect}
            options={periodOptions}
            value={
              periodOptions.flatMap((option) => option.options).find((o) => o.periodName === selectedPeriodNameOption)
                ?.value ?? undefined
            }
            placeholder="Select Period"
            onChange={(value): void => {
              handleSelectedPeriodChange({ value });
            }}
            usePortal
            disabled={!periodOptions}
          />
        </InputFormContainer>
      )}
      <ModalActions hasTopBorder={false}>
        <Button
          disabled={!isClosable || creatingFiscalYear}
          variant="white"
          rounded="regular"
          size="base"
          onClick={(): void => setIsOpen(false)}
          id={fiscalPeriodModalCancelBtnId}
        >
          Cancel
        </Button>
        <Button
          disabled={disabled}
          variant="primary"
          rounded="regular"
          size="base"
          onClick={handleSubmit}
          id={fiscalPeriodModalSelectBtnId}
          startIcon={creatingFiscalYear ? <Spinner size="sm" variant="white" /> : undefined}
        >
          {creatingFiscalYear ? 'Loading...' : 'Select'}
        </Button>
      </ModalActions>
    </Stack>
  );
};
